import { Injectable } from '@angular/core';
import { Filter } from 'src/app/shared/models/filter';
import { IFilterModel } from 'src/app/shared/models/filter-model';
import { InvoiceStatus } from '../../domain/enums/invoice-status.enum';

@Injectable()
export class InvoiceFilterAdapter {
  private readonly searchableFields = [
    'consumerName',
    'consumerTaxId',
    'issuerName',
    'issuerRegistrationNumber'
  ];

  private readonly defaultStatuses: InvoiceStatus[] = [
    InvoiceStatus.Issued,
    InvoiceStatus.RegisterError,
    InvoiceStatus.Created,
    InvoiceStatus.Failed,
    InvoiceStatus.Canceled,
    InvoiceStatus.SentEmail,
    InvoiceStatus.Pending
  ];

  createDefaultFilter(): Filter {
    const filter = new Filter().size(6);
    this.applyDefaultStatuses(filter);
    this.withCurrentMonth(filter, 'revenueDate');
    return filter;
  }

  createInitialFilterModels(groupId: string): readonly IFilterModel[] {
    return [this.createCurrentMonthFilterModel(groupId, 'revenueDate')];
  }

  withSearch(filter: Filter, text: string): Filter {
    const value = text.trim();

    if (value.length === 0) {
      filter.clearSearch();
      return filter;
    }

    filter.search(this.searchableFields, value);
    return filter;
  }

  withPage(filter: Filter, page: number): Filter {
    filter.page(page);
    return filter;
  }

  withCurrentMonth(filter: Filter, field: string): Filter {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    filter.setFilter('date', [
      { key: 'date', fields: ['after'], property: field, value: this.toLocalDateString(startDate) },
      { key: 'date', fields: ['before'], property: field, value: this.toLocalDateString(endDate) }
    ]);

    return filter;
  }

  createCurrentMonthFilterModel(groupId: string, field: string): IFilterModel {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    return {
      id: groupId,
      type: 'MONTH',
      key: 'date',
      data: [
        { id: 'after', name: startDate },
        { id: 'before', name: endDate }
      ],
      filter: [
        { key: 'date', fields: ['after'], property: field, value: this.toLocalDateString(startDate) },
        { key: 'date', fields: ['before'], property: field, value: this.toLocalDateString(endDate) }
      ]
    };
  }

  withFilterModels(filter: Filter, filters: readonly IFilterModel[]): Filter {
    filter.clearFilter('date');
    filter.clearFilter('professional');
    filter.clearFilter('status');

    const statusFilter = filters.find((item) => item.key === 'status');
    if (statusFilter?.filter.length) {
      filter.setFilter(statusFilter.key, statusFilter.filter);
    } else {
      this.applyDefaultStatuses(filter);
    }

    filters.forEach((item) => {
      if (item.key !== 'status' && item.filter.length > 0) {
        filter.setFilter(item.key, item.filter);
      }
    });

    return filter;
  }

  private applyDefaultStatuses(filter: Filter): void {
    this.defaultStatuses.forEach((status) => filter.filter('status', status, 'IN'));
  }

  private toLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
